/**
 * As-built COMBINE-VIEWS draft — pure logic.
 *
 * The ticket allows a surveyor to "use up to all three views to derive a single
 * point". A cloud pick in an orthographic slice resolves the TWO axes that view
 * can see; the third — the view's depth axis — is the position of whichever
 * surface the ray struck FIRST, which is not necessarily the feature being
 * measured. On a flat face square to the camera that depth is actually the most
 * stable of the three (2 mm spread measured, versus 145 mm in-plane at overview
 * zoom). Near a silhouette edge it is the least: roughly half of picks fall
 * through to the surface behind, and a 656 mm z-jump across 2 px was measured
 * directly. Depth is therefore not worthless, it is UNVERIFIABLE from one view,
 * so we take each axis only from a view that resolves it in-plane:
 *
 *     XY (Top / Bottom)  -> x, y
 *     XZ (North / South) -> x, z
 *     YZ (East / West)   -> y, z
 *
 * So a pick writes only its plane's two axes into a draft and leaves the third
 * alone. A second pick in the SAME plane overwrites only that plane's two axes.
 * The draft is committable only when all three axes are non-null.
 *
 * THE AGREEMENT GATE — why the overwrite is not housekeeping.
 * Any two planes share exactly one axis:
 *
 *     XY n XZ = x        XY n YZ = y        XZ n YZ = z
 *
 * so the pick that COMPLETES a point always re-measures an axis that is already
 * filled, from a different projection. The difference between the two readings
 * answers the only question that cannot otherwise be asked in a client-only
 * demo with no ground truth: did my two picks land on the SAME physical feature?
 *
 * Discarding it silently is the most dangerous failure this flow has. Pick the
 * base-plate corner in plan, switch to elevation where the support is a
 * different shape entirely, click what looks like the same corner but is the
 * plate edge 40 mm along, and the draft holds x,z from the second pick and y
 * from the first — three numbers describing NO POINT THAT EXISTS. Both picks
 * were on the same support inside the same 300 mm frame, so the composite lands
 * tens of mm from design: exactly the magnitude a real deviation has. It reads
 * as a plausible finding, and against a +/-3 mm tolerance it reads FAIL. Someone
 * raises a non-conformance against a correctly built support.
 *
 * Contrast the failures the spike already caught — an un-framed pick scatters
 * 145 mm, a silhouette pick jumps 656 mm. Those are VISIBLY wrong. This one is
 * not, which is what makes it worse.
 *
 * So a contribution whose shared axis disagrees by more than that axis's
 * tolerance is HELD, not applied, and the surveyor resolves it explicitly. The
 * whole contribution is held rather than just the offending axis: a disagreement
 * means the two picks are on different features, which makes the new pick's
 * OTHER axis equally suspect. One pick asserts one point on one feature; it is
 * accepted or rejected as a unit.
 *
 * UNITS: draft axes are VIEWER units, exactly as `enableCloudPick` delivers
 * them — metres on some facilities, millimetres on others (utils/modelUnits.js).
 * Nothing here scales a POSITION. The only millimetre values are `mmPerPx`,
 * which is a screen scale rather than a position, and the agreement gate's
 * `deltaMm`, which is a DIFFERENCE converted with a scale the caller supplies.
 *
 * PROVENANCE: each plane contribution records which scan it was measured
 * against and how coarse the view was (mm per pixel) AT PICK TIME. Both are
 * snapshotted, never read live at render — the selected scan changes whenever
 * the surveyor touches the scan selector, so a report that looked it up later
 * would label every point with the last-selected cloud.
 *
 * Everything here is pure and side-effect free so it can be exercised by a node
 * assertion script (`.claude/scratch/asBuiltDraft.assert.js`).
 */

/** Plane id -> the two axes that plane can resolve. THE single source of truth. */
export const PLANE_AXES = Object.freeze({
  XY: Object.freeze(['x', 'y']),
  XZ: Object.freeze(['x', 'z']),
  YZ: Object.freeze(['y', 'z']),
});

/** The unsliced spatial view. It is navigation only and can never resolve an axis. */
export const PLANE_3D = '3D';

/**
 * Named viewer view -> the slice plane it captures. THE PRIMARY DIRECTION.
 *
 * Each plane has an OPPOSITE PAIR of views looking down the same world axis
 * from either side, so this map is many-to-one and cannot be expressed as an
 * inversion of a plane -> view map. It used to be exactly that (one frozen
 * `VIEW_BY_CAPTURE_PLANE` plus a `reduce` that flipped it), which is a shape
 * that structurally cannot hold two views for one plane — hence the flip.
 *
 * Every id here MUST also exist in `verificationContext`'s view table and in
 * `computeViewPose`'s pose table; the three are read as one contract and a
 * missing entry fails silently in a different way in each.
 *
 * `iso` maps to `3D`, the unsliced navigation view, and is deliberately not
 * capture-capable. Dropping that entry does NOT disable Isometric loudly —
 * `WorkspacePanel.onViewChange` returns early on a null plane, so clicking it
 * would become a no-op with no log at all.
 */
export const CAPTURE_PLANE_BY_VIEW = Object.freeze({
  top: 'XY',
  bottom: 'XY',
  north: 'XZ',
  south: 'XZ',
  east: 'YZ',
  west: 'YZ',
  iso: PLANE_3D,
});

/**
 * Slice plane -> the view to show when NOBODY CHOSE ONE.
 *
 * Used only on paths that arrive with a plane and no view: a re-slice after the
 * design point moved, a recapture request, the legacy string `plane-change`
 * payload. When the surveyor did pick a view, that view is threaded through the
 * slice round trip instead — otherwise choosing `south` would apply the slice
 * and then snap both the camera and the combo label back to `north`, silently
 * mirroring the projection the reading is taken against.
 */
export const CANONICAL_VIEW_BY_PLANE = Object.freeze({
  XY: 'top',
  XZ: 'north',
  YZ: 'east',
  [PLANE_3D]: 'iso',
});

export function viewForCapturePlane(plane) {
  return CANONICAL_VIEW_BY_PLANE[plane] || null;
}

export function capturePlaneForView(viewId) {
  return CAPTURE_PLANE_BY_VIEW[viewId] || null;
}

/** Axis order used for every filled/missing list, so UI order is stable. */
export const AXIS_ORDER = Object.freeze(['x', 'y', 'z']);

/**
 * A brand-new, empty draft.
 * @param {string|null} pointId The point this draft belongs to.
 * @returns {{pointId: string|null, axes: {x: number|null, y: number|null, z: number|null}, contributions: Object}}
 */
export function emptyDraft(pointId = null) {
  return {
    pointId: pointId || null,
    axes: { x: null, y: null, z: null },
    // Which plane last wrote each axis. Recorded rather than inferred, so the
    // agreement gate can tell a cross-view CHECK from a same-view CORRECTION.
    axisSource: { x: null, y: null, z: null },
    // Keyed by plane id. Replaced wholesale on every merge — never mutated in
    // place, because Vue 2 cannot observe a key added to an existing object.
    contributions: {},
    // A contribution held by the agreement gate, awaiting an explicit
    // replace/keep. Never more than one: the gate blocks further merges until
    // it is resolved, so a queue could not form.
    pendingConflict: null,
  };
}

/** True when `plane` is one of the three slice planes. */
export function isSlicePlane(plane) {
  return Object.prototype.hasOwnProperty.call(PLANE_AXES, plane);
}

function isFiniteNumber(n) {
  return typeof n === 'number' && Number.isFinite(n);
}

/**
 * Axes this contribution would overwrite that were measured FROM A DIFFERENT
 * PLANE, with the disagreement on each in millimetres.
 *
 * `mmPerViewerUnit` arrives from the caller: the axes are VIEWER units, and
 * turning a gap between two picks into millimetres needs the model's scale.
 * This file used to keep a local `METRES_TO_MM = 1000` "so this module imports
 * nothing" — dependency-free was right, hard-coded was not. The scale is now a
 * parameter, which keeps both properties.
 *
 * THE "DIFFERENT PLANE" PART IS THE WHOLE POINT. The gate exists because two
 * planes share exactly one axis, so the second view re-measures it INDEPENDENTLY
 * — from a different projection, of what should be the same physical feature.
 * That independence is what makes the comparison evidence.
 *
 * Re-picking the SAME plane is not that. It is a CORRECTION: the surveyor looked
 * again and decided the first click was in the wrong place. Comparing the two
 * says only "you moved the cursor", and gating on it makes correcting a pick
 * impossible without adjudicating a disagreement with yourself. Observed live:
 * two XY picks 243 mm apart raised `DISAGREE X … XY … XY`, with the same view
 * named on both sides — a self-evidently meaningless comparison.
 *
 * So a same-plane re-pick falls straight through to `applyContribution`, which
 * overwrites that plane's two axes. That is exactly what the ticket specifies.
 */
function sharedAxisDisagreements(base, plane, point, mmPerViewerUnit) {
  const out = [];
  // No scale, no comparison. Every deltaMm would be NaN, every `deltaMm > tol`
  // false, and the gate would wave through exactly the silent cross-view
  // disagreement it exists to catch. Unreachable in practice — capture is gated
  // on the transform upstream — so refusing to judge is the honest answer.
  if (!isFiniteNumber(mmPerViewerUnit) || mmPerViewerUnit <= 0) return out;
  for (const axis of PLANE_AXES[plane]) {
    const previous = base.axes[axis];
    if (!isFiniteNumber(previous)) continue;
    const fromPlane = sourcePlaneOf(base, axis);
    if (!fromPlane || fromPlane === plane) continue;
    out.push({
      axis,
      previous,
      next: point[axis],
      deltaMm: Math.abs(point[axis] - previous) * mmPerViewerUnit,
      fromPlane,
    });
  }
  return out;
}

/**
 * Which plane last wrote `axis`. Null when nothing has.
 *
 * Read from `axisSource`, which records it at write time. Inferring it from the
 * order of `contributions` keys is wrong: the spread in `applyContribution`
 * updates an existing plane IN PLACE, so its key keeps the position of its FIRST
 * write. After XY, XZ, XY the keys still read `['XY','XZ']` and the inferred
 * answer for x would be XZ, when XY wrote it most recently.
 */
function sourcePlaneOf(draft, axis) {
  return draft?.axisSource?.[axis] || null;
}

/** Apply a contribution unconditionally. The gate decides whether to call this. */
function applyContribution(base, { pointId, plane, point, scanName, scanIsManual, mmPerPx }) {
  const axes = PLANE_AXES[plane];
  const nextAxes = { ...base.axes };
  const nextSource = { ...(base.axisSource || {}) };
  for (const axis of axes) {
    nextAxes[axis] = point[axis];
    nextSource[axis] = plane;
  }
  return {
    pointId: pointId || null,
    axes: nextAxes,
    axisSource: nextSource,
    contributions: {
      ...base.contributions,
      [plane]: {
        plane,
        axes: [...axes],
        // Keep the actual hit from this plane. The merged draft intentionally
        // discards the plane's depth axis, but the canvas marker must show where
        // the surveyor really clicked rather than inventing a composite point.
        point: { x: point.x, y: point.y, z: point.z },
        scanName: scanName || null,
        scanIsManual: !!scanIsManual,
        mmPerPx: isFiniteNumber(mmPerPx) ? mmPerPx : null,
      },
    },
    pendingConflict: null,
  };
}

/**
 * Merge one plane's contribution into a draft.
 *
 * Returns a NEW draft; the input is never mutated. Returns the input unchanged
 * when the plane is unknown or the picked point is missing an axis this plane
 * is responsible for — a bad pick must not silently blank a good axis.
 *
 * When `pointId` differs from the draft's own, the draft is restarted from
 * empty: a draft belongs to exactly one verification point.
 *
 * @param {Object} draft         Current draft (or null/undefined for a fresh one).
 * @param {Object} contribution
 * @param {string} contribution.pointId      Point the pick belongs to.
 * @param {'XY'|'XZ'|'YZ'} contribution.plane
 * @param {{x: number, y: number, z: number}} contribution.point Picked point, VIEWER units.
 * @param {string|null} [contribution.scanName]     Cloud the pick was taken against.
 * @param {boolean} [contribution.scanIsManual]     True when the surveyor chose that cloud by hand.
 * @param {number|null} [contribution.mmPerPx]      Worst-axis screen scale at pick time, mm/px.
 * @param {Object} [options] Agreement-gate configuration. Both members exist
 *        only so the gate can judge; neither is stored on the draft.
 * @param {{x:number,y:number,z:number}|null} [options.toleranceMm]
 *        Per-axis tolerance in MILLIMETRES, used only by the agreement gate. Omit it and
 *        disagreements are recorded but never block — there is nothing to judge against.
 * @param {number|null} [options.mmPerViewerUnit]
 *        Scale that turns the viewer-space gap between two picks into millimetres.
 *        It rides the mutation's payload from the dispatching action (a Vuex mutation
 *        cannot reach the store) and is lifted into options THERE, so it never lands
 *        in draft state where it could outlive the facility that produced it.
 * @returns {Object} New draft. The input is returned unchanged when the plane is unknown,
 *        the point is missing an axis that plane resolves, or a conflict is already pending.
 *        When the gate trips, the returned draft carries `pendingConflict` and its axes are
 *        NOT advanced until `resolvePendingConflict` is called.
 */
export function mergePlaneAxes(draft, contribution, options = {}) {
  const { pointId, plane, point, scanName = null, scanIsManual = false, mmPerPx = null } =
    contribution || {};
  const { toleranceMm = null, mmPerViewerUnit = null } = options || {};

  const base =
    draft && draft.pointId === (pointId || null) ? draft : emptyDraft(pointId);

  if (!isSlicePlane(plane)) return base;
  if (!point) return base;

  const axes = PLANE_AXES[plane];
  for (const axis of axes) {
    if (!isFiniteNumber(point[axis])) return base;
  }

  // An unresolved conflict blocks further picks. Accepting new readings on top
  // of a disagreement the surveyor has not adjudicated would bury the very
  // evidence the gate exists to surface.
  if (base.pendingConflict) return base;

  const staged = { pointId: pointId || null, plane, point, scanName, scanIsManual, mmPerPx };

  // Gate only where a tolerance exists to judge against. With none set we cannot
  // say whether a disagreement matters, so the reading is applied — but the
  // delta is still reported by `draftAxisFill` so the UI can show it.
  // Each disagreement carries the tolerance it was judged against, so the UI and
  // the report can state the comparison without re-deriving it from the point —
  // by the time either reads this, the surveyor may have edited the tolerance.
  const gated = sharedAxisDisagreements(base, plane, point, mmPerViewerUnit)
    .map((d) => ({
      ...d,
      toleranceMm:
        toleranceMm && isFiniteNumber(toleranceMm[d.axis]) ? toleranceMm[d.axis] : null,
    }))
    .filter((d) => d.toleranceMm !== null && d.deltaMm > d.toleranceMm);

  if (gated.length) {
    return {
      ...base,
      pendingConflict: {
        plane,
        contribution: { ...staged, point: { ...point } },
        disagreements: gated,
        worstDeltaMm: gated.reduce((m, d) => Math.max(m, d.deltaMm), 0),
      },
    };
  }

  return applyContribution(base, staged);
}

/**
 * Adjudicate a held contribution.
 *
 * @param {Object} draft
 * @param {'replace'|'keep'} choice  `replace` applies the held pick in full;
 *                                   `keep` discards it and leaves the draft as it was.
 * @returns {Object} New draft. Unchanged when nothing is pending or `choice` is unknown.
 */
export function resolvePendingConflict(draft, choice) {
  const pending = draft?.pendingConflict;
  if (!pending) return draft || emptyDraft(null);

  const cleared = { ...draft, pendingConflict: null };
  if (choice === 'keep') return cleared;
  if (choice === 'replace') return applyContribution(cleared, pending.contribution);
  return draft;
}

/**
 * Which axes are filled, and everything the "X ok  Y ok  Z —" indicator needs.
 *
 * @param {Object} draft
 * @returns {{pointId: string|null, axes: Object, filled: {x: boolean, y: boolean, z: boolean},
 *   filledAxes: string[], missingAxes: string[], isComplete: boolean,
 *   planes: string[], scanNames: string[], scanIsManual: boolean, worstMmPerPx: number|null}}
 */
export function draftAxisFill(draft) {
  const d = draft || emptyDraft(null);
  const axes = d.axes || { x: null, y: null, z: null };

  const filled = {};
  const filledAxes = [];
  const missingAxes = [];
  for (const axis of AXIS_ORDER) {
    const ok = isFiniteNumber(axes[axis]);
    filled[axis] = ok;
    (ok ? filledAxes : missingAxes).push(axis);
  }

  const contributions = Object.values(d.contributions || {});
  const scanNames = [];
  let scanIsManual = false;
  // Worst (largest) mm/px across every contributing pick. The only honest claim
  // a report can make about a point assembled from several views is "no better
  // than N mm per pixel" — averaging would flatter the coarsest reading.
  let worstMmPerPx = null;
  for (const c of contributions) {
    if (c.scanName && !scanNames.includes(c.scanName)) scanNames.push(c.scanName);
    if (c.scanIsManual) scanIsManual = true;
    if (isFiniteNumber(c.mmPerPx) && (worstMmPerPx === null || c.mmPerPx > worstMmPerPx)) {
      worstMmPerPx = c.mmPerPx;
    }
  }

  return {
    pointId: d.pointId || null,
    axes: { ...axes },
    filled,
    filledAxes,
    missingAxes,
    isComplete: missingAxes.length === 0,
    // isComplete asks "are all three axes filled". isCommittable adds "and is
    // nothing disputed". They differ exactly when the gate is holding a pick,
    // which is the moment "Use this point" must stay disabled.
    isCommittable: missingAxes.length === 0 && !d.pendingConflict,
    hasConflict: !!d.pendingConflict,
    pendingConflict: d.pendingConflict || null,
    planes: Object.keys(d.contributions || {}),
    scanNames,
    scanIsManual,
    worstMmPerPx,
  };
}

/**
 * The draft as a point, or null while any axis is missing OR a disagreement is
 * unresolved. Committing a disputed draft is the failure this module exists to
 * prevent, so the gate is enforced here too — not only in the UI.
 * @returns {{x: number, y: number, z: number}|null} WORLD METRES.
 */
export function draftToPoint(draft) {
  const fill = draftAxisFill(draft);
  if (!fill.isCommittable) return null;
  return { x: fill.axes.x, y: fill.axes.y, z: fill.axes.z };
}
